/* =========================================================================
   Account menu — a persistent gold-ring avatar button pinned top-right, with a
   dollars badge (authed → wallet from /profile/me; guest → current session
   bank). Visible on the menu-ish screens (home / results / history) AND, since
   the 2026-08-21 design round (§A), mid-round: on `play` it is re-parented into
   the HUD's ⋯ slot (ui/playHud.ts) and its first item is "Save & leave", so a
   player on Q3/10 is never trapped. Tapping opens a game-styled dropdown that
   adapts to signed-in vs guest state.

   Signed in: header (name + "$X in the bank" + rank) and Achievements /
   History / Sign out / danger-styled Delete account (double-confirm →
   DELETE /api/profile/me → logout + resetGuestId + welcome).
   Guest: "Playing as guest" header, Create free account / Sign in, and a
   session-achievements view (or a sign-in nudge). Row glyphs are brand line
   icons (ui/icons.ts), never emoji.
   ========================================================================= */
import { byId } from './dom';
import { ctx } from './context';
import { fmtCents, esc } from './engine';
import { track } from './telemetry';
import { resetGuestId } from './guestId';
import * as api from './api';
import { showHistory, showRegister, showLogin, showWelcome, showHome } from './auth';
import { consumePostAuthResume } from './postAuthResume';
import { currentTier, setAuthedHistory } from './progression';
import { resetGtkyEngagement } from './gtkyCadence';
import { openCollections } from './collections';
import { openMonastery, resetMonasterySinks } from './monastery';
import { resetAlms } from './almsgiving';
import { openLeaderboard } from './leaderboard';
import { forgetGtky } from './characters/memory';
import { PLAYER_NAME_KEY } from './characters/name';
import { resetArcadeUnlock } from './arcade';
import { clearSnapshot } from './resumeGame';
import { refreshHomeSurfaces } from './homeGate';
import { isIosWrapper, enablePush, disablePush } from './notifications';
import { CLIENT_VERSION } from './version';
import { triggerCharPop } from './characters/ui';
import { openLeaveRound, initPlayHud, resetCareerLevel, abortPendingRound } from './gameController';
import { resetAnswerQueue } from './answerQueue';
import { syncPlayHud, HUD_SCREENS } from './ui/playHud';
import { icon, isIconName, type IconName } from './ui/icons';

/** Screens on which the avatar button is visible. */
// Visible on every "section" screen so it works as persistent navigation, on the
// round-intro / between-rounds pause points (dropped below the bank pill; Home is
// labeled as ending the run), and — design round 2026-08-21 §A — on `play` and
// `preroll`, where it lives in the HUD's ⋯ slot and leads with "Save & leave".
// Mini-game breaks stay chrome-free.
const MENU_SCREENS = new Set(['home', 'results', 'history', 'welcome', 'login', 'register', 'roundintro', 'nextround', 'play', 'preroll', 'feast', 'collections', 'pilgrimage', 'monastery', 'leaderboard', 'friendly']);
// NB: 'friendlyplay' (the study card) is deliberately absent — a study set has
// no run to save; the picker ('friendly') is a content screen and carries the
// persistent nav like Collections/Leaderboard do.
const PAUSE_SCREENS = new Set(['roundintro', 'nextround']); // mid-game pause: bank pill visible
let currentScreen = 'welcome';

let walletCents: number | null = null; // last-known authed wallet balance

/* ---------- feedback (db/035) ----------
   The category whitelist is mirrored server-side; keep the two in step. */
export const FEEDBACK_CATEGORIES = ['Bug', 'Idea', 'Question', 'Content correction', 'Other'] as const;
export const FEEDBACK_MIN = 10;
export const FEEDBACK_MAX = 2000;
export const FEEDBACK_DRAFT_KEY = 'catholics.feedbackDraft';

export const accountMenuHTML = /* html */ `
<div id="accountMenu" class="acct hidden">
  <button id="acctBtn" class="acct-btn" aria-label="Account menu" aria-haspopup="true">
    <span class="acct-avatar" id="acctAvatar">${icon('user', 20)}</span>
    <span class="acct-badge money" id="acctBadge">$0</span>
  </button>
  <div id="acctPanel" class="acct-panel hidden" role="menu" aria-hidden="true">
    <!-- MAIN VIEW -->
    <div id="acctMain">
      <div class="acct-head">
        <div class="acct-head-avatar" id="acctHeadAvatar">${icon('user', 20)}</div>
        <div class="acct-head-info">
          <div class="acct-name" id="acctName">Playing as guest</div>
          <div class="acct-sub" id="acctSub"></div>
        </div>
      </div>
      <div class="acct-items" id="acctItems"></div>
    </div>
    <!-- ACHIEVEMENTS VIEW -->
    <div id="acctAch" class="acct-ach-view hidden">
      <button class="acct-ach-back" id="acctAchBack">← Back</button>
      <div class="acct-ach-head">${icon('trophy', 18)} Achievements</div>
      <div class="acct-ach-list" id="acctAchList"></div>
    </div>
    <!-- NOTIFICATIONS VIEW -->
    <div id="acctNotif" class="acct-ach-view hidden">
      <button class="acct-ach-back" id="acctNotifBack">← Back</button>
      <div class="acct-ach-head">${icon('bell', 18)} Notifications</div>
      <div class="acct-notif-topics" id="acctNotifTopics"></div>
      <button class="btn" id="acctNotifToggle"></button>
      <div class="acct-notif-hint" id="acctNotifHint"></div>
    </div>
    <!-- FEEDBACK VIEW -->
    <div id="acctFeedback" class="acct-ach-view hidden">
      <button class="acct-ach-back" id="acctFbBack">← Back</button>
      <div class="acct-ach-head">${icon('chat', 18)} Send feedback</div>
      <div class="acct-fb-chips" id="acctFbChips" role="radiogroup" aria-label="What kind of note is this?"></div>
      <textarea id="acctFbMsg" class="acct-fb-msg" rows="5" maxlength="${FEEDBACK_MAX}"
                placeholder="Tell us what you noticed, what you'd love, or what went wrong…"
                aria-label="Your feedback"></textarea>
      <div class="acct-fb-count" id="acctFbCount"></div>
      <input id="acctFbEmail" class="acct-fb-email hidden" type="email" maxlength="256"
             placeholder="Email, only if you'd like a reply" autocomplete="email" />
      <button class="btn" id="acctFbSend">Send</button>
      <div class="acct-fb-note" id="acctFbNote"></div>
    </div>
  </div>
</div>

<!-- DELETE-ACCOUNT CONFIRM (double-confirm; danger) -->
<div id="acctConfirm" class="acct-confirm hidden" aria-hidden="true">
  <div class="ac-dim" id="acctConfirmDim"></div>
  <div class="ac-card">
    <div class="ac-title">Delete account?</div>
    <div class="ac-body">This permanently deletes your account and anonymizes your scores. This cannot be undone.</div>
    <button class="btn ac-danger" id="acctConfirmDelete">Delete my account</button>
    <button class="btn secondary" id="acctConfirmCancel">Cancel</button>
  </div>
</div>

<!-- TOAST -->
<div id="acctToast" class="acct-toast hidden" role="status" aria-live="polite"></div>`;

/* ---------- toast ---------- */
let toastTimer: ReturnType<typeof setTimeout> | null = null;
function toast(msg: string, kind: 'ok' | 'err' = 'ok'): void {
  const t = byId('acctToast');
  t.textContent = msg;
  t.className = 'acct-toast ' + kind;
  void t.offsetWidth;
  t.classList.add('show');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    t.classList.remove('show');
    t.classList.add('hidden');
  }, 3200);
}

/* ---------- panel open/close ---------- */
function isPanelOpen(): boolean {
  return !byId('acctPanel').classList.contains('hidden');
}
function openPanel(): void {
  showMainView();
  renderPanel();
  const p = byId('acctPanel');
  p.classList.remove('hidden');
  p.setAttribute('aria-hidden', 'false');
  void p.offsetWidth;
  p.classList.add('show');
  track('AccountMenuOpened');
  if (api.isAuthenticated()) void refreshProfile();
}
function closePanel(): void {
  parkFeedbackDraftIfAny();
  const p = byId('acctPanel');
  p.classList.add('hidden');
  p.classList.remove('show');
  p.setAttribute('aria-hidden', 'true');
}
function togglePanel(): void {
  if (isPanelOpen()) closePanel();
  else openPanel();
}

function showMainView(): void {
  byId('acctMain').classList.remove('hidden');
  byId('acctAch').classList.add('hidden');
  byId('acctNotif').classList.add('hidden');
  byId('acctFeedback').classList.add('hidden');
}
function showAchView(): void {
  byId('acctMain').classList.add('hidden');
  byId('acctAch').classList.remove('hidden');
  void renderAchievements();
}
function showNotifView(): void {
  byId('acctMain').classList.add('hidden');
  byId('acctNotif').classList.remove('hidden');
  renderNotifView();
}
function showFeedbackView(): void {
  byId('acctMain').classList.add('hidden');
  byId('acctFeedback').classList.remove('hidden');
  renderFeedbackView();
}

/* ---------- button (avatar + badge) ---------- */
function renderButton(): void {
  const avatar = byId('acctAvatar');
  const badge = byId('acctBadge');
  if (api.isAuthenticated()) {
    const u = api.getCurrentUser();
    const initial = (u?.displayName?.trim()?.charAt(0) || '★').toUpperCase();
    avatar.textContent = initial;
    avatar.classList.remove('guest');
    badge.textContent = fmtCents(walletCents ?? 0);
  } else {
    avatar.innerHTML = icon('user', 20);
    avatar.classList.add('guest');
    badge.textContent = fmtCents(ctx.game.bank || 0); // current session bank
  }
}

async function refreshProfile(): Promise<void> {
  const r = await api.getProfileMe();
  if (!r.ok) return;
  walletCents = r.data.stats?.walletBalanceCents ?? 0;
  renderButton();
  if (isPanelOpen()) renderPanel();
}

/* ---------- panel contents ---------- */
/** Exported: homeFab.ts reuses this so every way home shares one behavior. */
export function goHome(): void {
  closePanel();
  // A mid-game auth round-trip may have armed a resume; choosing Home is an explicit
  // exit from that flow — discard it so a later sign-in doesn't teleport into a dead game.
  consumePostAuthResume();
  abortPendingRound(); // "End game & go Home" inside the 1.4s launch beat must not pop the next round over home
  byId('bankbar').classList.add('hidden');
  showHome();
}

function renderPanel(): void {
  const authed = api.isAuthenticated();
  const headAvatar = byId('acctHeadAvatar');
  const name = byId('acctName');
  const sub = byId('acctSub');
  const items = byId('acctItems');
  items.innerHTML = '';

  // Persistent way back to the hub from any section (hidden when already home).
  // Mid-ROUND (play/preroll) the first item is the leave sheet — bank + finished
  // rounds are saved, the round in progress restarts. On the between-rounds
  // pause screens the label says what really happens — the run is abandoned.
  if (HUD_SCREENS.has(currentScreen)) {
    addItem(items, 'pause', 'Save & leave', () => {
      closePanel();
      openLeaveRound();
    });
  } else if (currentScreen !== 'home') {
    addItem(items, 'home', PAUSE_SCREENS.has(currentScreen) ? 'End game & go Home' : 'Home', goHome);
  }

  // Deeper surfaces (Tier 1 only; guests included) — moved off the home screen so
  // the new-player home stays to one clear action. Right after Home in the menu.
  if (currentTier() === 1) {
    addItem(items, 'cards', 'Collections', () => {
      closePanel();
      byId('bankbar').classList.add('hidden');
      void openCollections();
    });
    addItem(items, 'church', 'Monastery', () => {
      closePanel();
      byId('bankbar').classList.add('hidden');
      void openMonastery();
    });
  }
  // All tiers, guests included (Eric 2026-09-06: a plain menu item, not the
  // trophy icon that used to dock under the avatar — and low in the list,
  // after Achievements, not the first thing in the menu).
  const addLeaderboard = (): void =>
    addItem(items, 'trophy', 'Leaderboard', () => {
      closePanel();
      byId('bankbar').classList.add('hidden');
      void openLeaderboard();
    });

  if (authed) {
    const u = api.getCurrentUser();
    headAvatar.textContent = (u?.displayName?.trim()?.charAt(0) || '★').toUpperCase();
    headAvatar.classList.remove('guest');
    name.textContent = u?.displayName || 'Your account';
    sub.textContent = walletCents == null ? 'Loading…' : `${fmtCents(walletCents)} in the bank`;
    addItem(items, 'trophy', 'Achievements', showAchView);
    addLeaderboard();
    addItem(items, 'book', 'History', () => {
      closePanel();
      void showHistory('home');
    });
    // iOS-wrapper-only for now (Phase 1) — Android/browser Web Push lands in Phase 2,
    // at which point this gates on a broader "can this device receive push" check.
    if (isIosWrapper()) addItem(items, 'bell', 'Notifications', showNotifView);
    addItem(items, 'chat', 'Send feedback', showFeedbackView);
    addItem(items, 'signout', 'Sign out', () => void handleSignOut());
    addItem(items, 'trash', 'Delete account', openDeleteConfirm, 'danger');
  } else {
    headAvatar.innerHTML = icon('user', 20);
    headAvatar.classList.add('guest');
    name.textContent = 'Playing as guest';
    sub.textContent = `${fmtCents(ctx.game.bank || 0)} this session`;
    addItem(items, 'chat', 'Send feedback', showFeedbackView);
    addItem(items, 'sparkle', 'Create free account', () => {
      closePanel();
      byId('bankbar').classList.add('hidden');
      showRegister();
    });
    addItem(items, 'key', 'Sign in', () => {
      closePanel();
      byId('bankbar').classList.add('hidden');
      showLogin();
    });
    addItem(items, 'trophy', 'Achievements', showAchView);
    addLeaderboard();
  }
}

function addItem(
  container: HTMLElement,
  ic: IconName,
  label: string,
  onClick: () => void,
  variant: '' | 'danger' = '',
): void {
  const b = document.createElement('button');
  b.className = 'acct-item' + (variant ? ' ' + variant : '');
  b.innerHTML = `<span class="acct-item-ic">${icon(ic, 22)}</span><span>${esc(label)}</span>`;
  b.addEventListener('click', onClick);
  container.appendChild(b);
}

/* ---------- achievements view ---------- */
async function renderAchievements(): Promise<void> {
  const list = byId('acctAchList');
  if (!api.isAuthenticated()) {
    // Guests: show this-session achievements, or a friendly sign-in nudge.
    const earned = ctx.game.achievements || [];
    if (earned.length === 0) {
      list.innerHTML = `<div class="acct-ach-empty">Sign in to collect achievements. They'll be saved to your account and show up here.</div>`;
      return;
    }
    list.innerHTML = earned.map((a) => achChip(a.icon, a.name, a.desc)).join('');
    return;
  }
  list.innerHTML = `<div class="acct-ach-empty">Loading…</div>`;
  const r = await api.getAchievements();
  if (!r.ok) {
    list.innerHTML = `<div class="acct-ach-empty">${r.offline ? 'Offline. Achievements will appear when you reconnect.' : 'Could not load achievements.'}</div>`;
    return;
  }
  if (r.data.length === 0) {
    list.innerHTML = `<div class="acct-ach-empty">No achievements yet. Play a full game to start earning them.</div>`;
    return;
  }
  list.innerHTML = r.data
    .map((a) => {
      const when = new Date(a.earnedUtc);
      const date = isNaN(when.getTime()) ? '' : when.toLocaleDateString();
      return achChip(a.icon, a.name, a.descr, date);
    })
    .join('');
}

function achChip(glyph: string | null | undefined, name: string, desc: string, date = ''): string {
  const meta = date ? `<div class="ach-desc">${esc(desc)} · ${esc(date)}</div>` : `<div class="ach-desc">${esc(desc)}</div>`;
  // A brand IconName renders as SVG; a legacy/server emoji glyph is escaped as-is;
  // the FALLBACK is the brand star.
  const face = !glyph ? icon('star', 24) : isIconName(glyph) ? icon(glyph, 24) : esc(glyph);
  return (
    `<div class="ach-chip"><span class="ach-ic">${face}</span>` +
    `<span><div class="ach-name">${esc(name)}</div>${meta}</span></div>`
  );
}

/* ---------- notifications view ---------- */
const NOTIF_TOPICS_KEY = 'catholics.notifTopics';
const NOTIF_ENABLED_KEY = 'catholics.pushEnabled';
const NOTIFICATION_TOPICS: { key: string; label: string }[] = [
  { key: 'daily-readings', label: 'Daily Readings' },
  { key: 'feast-days', label: 'Feast Days' },
  { key: 'parish-announcements', label: 'Parish Announcements' },
];

function getSelectedTopics(): Set<string> {
  try {
    const raw = localStorage.getItem(NOTIF_TOPICS_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {
    /* fall through to default */
  }
  return new Set(NOTIFICATION_TOPICS.map((t) => t.key)); // default: everything on
}
function setSelectedTopics(topics: Set<string>): void {
  try {
    localStorage.setItem(NOTIF_TOPICS_KEY, JSON.stringify([...topics]));
  } catch {
    /* private mode / eviction — selection just won't persist across sessions */
  }
}
function isPushEnabled(): boolean {
  try {
    return localStorage.getItem(NOTIF_ENABLED_KEY) === '1';
  } catch {
    return false;
  }
}
function setPushEnabled(enabled: boolean): void {
  try {
    if (enabled) localStorage.setItem(NOTIF_ENABLED_KEY, '1');
    else localStorage.removeItem(NOTIF_ENABLED_KEY);
  } catch {
    /* private mode / eviction */
  }
}

function renderNotifView(): void {
  const topicsEl = byId('acctNotifTopics');
  const selected = getSelectedTopics();
  const enabled = isPushEnabled();

  topicsEl.innerHTML = NOTIFICATION_TOPICS.map(
    (t) =>
      `<label class="acct-notif-topic"><input type="checkbox" data-topic="${esc(t.key)}" ${selected.has(t.key) ? 'checked' : ''}/> ${esc(t.label)}</label>`,
  ).join('');
  topicsEl.querySelectorAll<HTMLInputElement>('input[data-topic]').forEach((input) => {
    input.addEventListener('change', () => void handleTopicToggle(input.dataset.topic!, input.checked));
  });

  const toggleBtn = byId<HTMLButtonElement>('acctNotifToggle');
  toggleBtn.textContent = enabled ? 'Turn off notifications' : 'Enable notifications';
  toggleBtn.className = 'btn' + (enabled ? ' secondary' : '');
  toggleBtn.onclick = () => void handlePushToggle(!enabled);

  byId('acctNotifHint').textContent = enabled
    ? 'You’ll get pushes for the topics checked above.'
    : 'Choose what you want to hear about, then enable notifications.';
}

async function handleTopicToggle(topic: string, checked: boolean): Promise<void> {
  const selected = getSelectedTopics();
  if (checked) selected.add(topic);
  else selected.delete(topic);
  setSelectedTopics(selected);
  if (isPushEnabled()) await api.putNotificationPreferences([...selected]);
}

async function handlePushToggle(turnOn: boolean): Promise<void> {
  const toggleBtn = byId<HTMLButtonElement>('acctNotifToggle');
  toggleBtn.disabled = true;
  if (turnOn) {
    const ok = await enablePush([...getSelectedTopics()]);
    setPushEnabled(ok);
    toast(ok ? 'Notifications enabled.' : 'Could not enable notifications. Check permission in Settings.', ok ? 'ok' : 'err');
  } else {
    await disablePush();
    setPushEnabled(false);
    toast('Notifications turned off.');
  }
  toggleBtn.disabled = false;
  renderNotifView();
}

/* ---------- feedback view (db/035) ---------- */

export interface FeedbackDraft {
  category: string;
  message: string;
  email?: string;
}

/** Read the parked draft. Anything malformed reads as "no draft" — a corrupt
    key must never break the form open. */
export function loadFeedbackDraft(): FeedbackDraft | null {
  try {
    const raw = localStorage.getItem(FEEDBACK_DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<FeedbackDraft>;
    if (typeof d?.message !== 'string') return null;
    return {
      category: typeof d.category === 'string' ? d.category : '',
      message: d.message,
      email: typeof d.email === 'string' ? d.email : undefined,
    };
  } catch {
    return null;
  }
}

/** Park what the player typed so a failed/offline send never loses it. */
export function saveFeedbackDraft(draft: FeedbackDraft): void {
  try {
    localStorage.setItem(FEEDBACK_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* private mode / eviction — the in-form text is still there for a retry */
  }
}

export function clearFeedbackDraft(): void {
  try {
    localStorage.removeItem(FEEDBACK_DRAFT_KEY);
  } catch {
    /* nothing to do */
  }
}

/** Client-side mirror of the server's validation, so a doomed note never costs
    a round trip. Returns the message to show, or null when it's good to send. */
export function validateFeedback(category: string, message: string): string | null {
  if (!(FEEDBACK_CATEGORIES as readonly string[]).includes(category)) return 'Pick what kind of note this is first.';
  const len = message.trim().length;
  if (len < FEEDBACK_MIN) return `A few more words, please. At least ${FEEDBACK_MIN} characters.`;
  if (len > FEEDBACK_MAX) return `That's a bit long. Keep it under ${FEEDBACK_MAX} characters.`;
  return null;
}

/** Currently-picked chip ('' = nothing picked yet; forces a deliberate choice). */
let fbCategory = '';

function renderFeedbackView(): void {
  const draft = loadFeedbackDraft();
  fbCategory = draft && (FEEDBACK_CATEGORIES as readonly string[]).includes(draft.category) ? draft.category : '';

  const msg = byId<HTMLTextAreaElement>('acctFbMsg');
  msg.value = draft?.message ?? '';
  msg.oninput = updateFbCount;

  const email = byId<HTMLInputElement>('acctFbEmail');
  // Authed players never see the email field — the server already has it.
  email.classList.toggle('hidden', api.isAuthenticated());
  email.value = api.isAuthenticated() ? '' : (draft?.email ?? '');

  renderFbChips();
  updateFbCount();

  byId('acctFbNote').textContent = `Sent with your note: app version ${CLIENT_VERSION}, current screen (${currentScreen}).`;

  const send = byId<HTMLButtonElement>('acctFbSend');
  send.disabled = false;
  send.textContent = 'Send';
  send.onclick = () => void handleFeedbackSubmit();
}

function renderFbChips(): void {
  const wrap = byId('acctFbChips');
  wrap.innerHTML = FEEDBACK_CATEGORIES.map(
    (c) =>
      `<button type="button" class="acct-fb-chip${c === fbCategory ? ' on' : ''}" role="radio" ` +
      `aria-checked="${c === fbCategory}" data-cat="${esc(c)}">${esc(c)}</button>`,
  ).join('');
  wrap.querySelectorAll<HTMLButtonElement>('button[data-cat]').forEach((b) => {
    b.addEventListener('click', () => {
      fbCategory = b.dataset.cat || '';
      renderFbChips(); // single-select: repaint so only one reads as on
    });
  });
}

function updateFbCount(): void {
  const len = byId<HTMLTextAreaElement>('acctFbMsg').value.trim().length;
  const el = byId('acctFbCount');
  el.textContent = `${len} / ${FEEDBACK_MAX}`;
  el.classList.toggle('short', len > 0 && len < FEEDBACK_MIN);
}

/** Dismissing the panel with words still in the box parks them under the same
    key a failed send uses — an accidental backdrop tap must not cost the note. */
function parkFeedbackDraftIfAny(): void {
  const view = document.getElementById('acctFeedback');
  if (!view || view.classList.contains('hidden')) return;
  const draft = currentFeedbackDraft();
  if (draft.message.trim().length > 0) saveFeedbackDraft(draft);
}

function currentFeedbackDraft(): FeedbackDraft {
  const email = byId<HTMLInputElement>('acctFbEmail').value.trim();
  return {
    category: fbCategory,
    message: byId<HTMLTextAreaElement>('acctFbMsg').value,
    ...(api.isAuthenticated() || !email ? {} : { email }),
  };
}

async function handleFeedbackSubmit(): Promise<void> {
  const draft = currentFeedbackDraft();
  const problem = validateFeedback(draft.category, draft.message);
  if (problem) {
    toast(problem, 'err');
    return;
  }

  const send = byId<HTMLButtonElement>('acctFbSend');
  send.disabled = true;
  send.textContent = 'Sending…';
  const r = await api.postFeedback({
    category: draft.category,
    message: draft.message.trim(),
    email: draft.email,
    clientVersion: CLIENT_VERSION,
    screen: currentScreen,
  });
  send.disabled = false;
  send.textContent = 'Send';

  if (r.ok) {
    track('FeedbackSubmitted', { category: draft.category });
    clearFeedbackDraft();
    fbCategory = '';
    byId<HTMLTextAreaElement>('acctFbMsg').value = '';
    byId<HTMLInputElement>('acctFbEmail').value = '';
    showMainView();
    closePanel();
    triggerCharPop('Thank you. We read every note.');
    return;
  }

  // Anything short of a confirmed save keeps the words: the form stays as typed
  // AND the draft is parked so a reload/next open restores it.
  saveFeedbackDraft(draft);
  toast(
    r.offline
      ? "Saved your note. We'll send it next time you're online."
      : r.error || 'Could not send just now. Your note is saved.',
    'err',
  );
}

/* ---------- sign out / delete ---------- */

/** Logout hygiene — leaving an AUTHED session must read as a clean slate on a
    (possibly shared) device: forget the cached name (Theo must not greet the
    previous owner), drop any un-sent guest name, re-lock the Arcade, discard the
    resume snapshot, reset the disclosure-tier override, and refresh the home
    surfaces so nothing stale survives the 5-min greeting throttle. Pure-guest
    sessions never route here, so a guest's own told-to-Theo name is kept. */
function scrubAuthedDeviceState(): void {
  // FULL clean slate (QA audit #2): sweep every catholics.* key EXCEPT the guest
  // device identity — the previous partial scrub left theoIntroduced / lifetime /
  // gamesCompleted etc. behind, and the boot migration re-seeded the Theo card,
  // so a shared device kept greeting the previous owner's progression.
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith('catholics.') && k !== 'catholics.guestId') localStorage.removeItem(k);
    }
    sessionStorage.clear();
  } catch {
    /* storage unavailable */
  }
  forgetGtky(PLAYER_NAME_KEY); // defensive (covered by the sweep)
  api.clearGuestName();
  resetArcadeUnlock();
  clearSnapshot();
  setAuthedHistory(false);
  resetGtkyEngagement(); // GTKY gate: the next player starts un-engaged (module memory)
  // db/047 sinks are per-account, and both live in module memory rather than
  // localStorage, so the key sweep above cannot reach them.
  resetMonasterySinks();
  resetAlms();
  resetCareerLevel(); // module memory too: the next player must not inherit "Level 12" / skip the warm-up
  resetAnswerQueue(); // queued answer rows belong to the signed-out account's sessions
  refreshHomeSurfaces();
}

async function handleSignOut(): Promise<void> {
  closePanel();
  await api.logout();
  walletCents = null;
  scrubAuthedDeviceState();
  showWelcome();
  syncAccountMenu('welcome'); // welcome now shows the guest variant (persistent nav); resets state
  toast('Signed out.');
}

function openDeleteConfirm(): void {
  const c = byId('acctConfirm');
  c.classList.remove('hidden');
  c.setAttribute('aria-hidden', 'false');
}
function closeDeleteConfirm(): void {
  const c = byId('acctConfirm');
  c.classList.add('hidden');
  c.setAttribute('aria-hidden', 'true');
}
async function handleDelete(): Promise<void> {
  const btn = byId<HTMLButtonElement>('acctConfirmDelete');
  btn.disabled = true;
  btn.textContent = 'Deleting…';
  const r = await api.deleteAccount();
  btn.disabled = false;
  btn.textContent = 'Delete my account';
  closeDeleteConfirm();
  if (r.ok) {
    // Server anonymized the account + revoked tokens; api.deleteAccount cleared
    // local auth. Regenerate the guest id so the device counts as a new guest.
    walletCents = null;
    resetGuestId();
    scrubAuthedDeviceState();
    closePanel();
    showWelcome();
    syncAccountMenu('welcome');
    toast('Your account was deleted. Take care.');
  } else {
    toast(r.offline ? 'Offline. Try again when connected.' : 'Could not delete the account. Please try again.', 'err');
  }
}

/* ---------- lifecycle ---------- */
export function initAccountMenu(): void {
  initPlayHud(); // play HUD ◀ + the leave sheet (shares this boot point; see ui/playHud.ts)
  byId('acctBtn').addEventListener('click', (e) => {
    e.stopPropagation();
    togglePanel();
  });
  byId('acctPanel').addEventListener('click', (e) => e.stopPropagation()); // taps inside stay open
  byId('acctAchBack').addEventListener('click', showMainView);
  byId('acctNotifBack').addEventListener('click', showMainView);
  byId('acctFbBack').addEventListener('click', showMainView);
  byId('acctConfirmDelete').addEventListener('click', () => void handleDelete());
  byId('acctConfirmCancel').addEventListener('click', closeDeleteConfirm);
  byId('acctConfirmDim').addEventListener('click', closeDeleteConfirm);
  // outside tap closes the panel
  document.addEventListener('click', () => {
    if (isPanelOpen()) closePanel();
  });
  // Escape closes panel (then the confirm dialog if it's up)
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (!byId('acctConfirm').classList.contains('hidden')) closeDeleteConfirm();
    else if (isPanelOpen()) closePanel();
  });
}

/** Show/hide the button per screen and refresh its avatar + badge. Also renders
    the guest variant whenever the token has expired / the player is logged out. */
export function syncAccountMenu(screen: string): void {
  currentScreen = screen;
  syncPlayHud(screen); // bankbar off + avatar into the HUD slot on play; restored elsewhere
  const wrap = byId('accountMenu');
  const visible = MENU_SCREENS.has(screen);
  wrap.classList.toggle('hidden', !visible);
  // Preroll keeps the top bankbar, so the avatar drops below it as on the pause screens.
  wrap.classList.toggle('below-bank', PAUSE_SCREENS.has(screen) || screen === 'preroll');
  if (!visible) {
    closePanel();
    return;
  }
  renderButton();
  if (api.isAuthenticated()) void refreshProfile();
}
